import { useCallback } from 'react';
import {
    RecoilState,
    useRecoilCallback,
    useRecoilValue,
    useSetRecoilState,
} from 'recoil';
import { AnnotationRecord } from '~/domain/annotation/annotation';
import { PointMm } from '~/domain/geometry/patientGeometry';

import { useImageForSeries } from '~/hooks/image/useImageForSeries';
import { localization } from '~/i18n';
import {
    AnnotationListState,
    clearAnnotations,
    removeAnnotation,
    updateAnnotation,
} from '~/state/atoms/annotationListAtoms';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { enqueueSnackbar } from '~/utils/notifications';
import { goToPlanePoint } from '~/hooks/viewer/goToPlanePoint';

/** The warning of a go-to on an annotation whose image no tile shows. */
const IMAGE_NOT_SHOWN_MESSAGE_ID = 'annotation.goTo.imageNotShown';

export interface AnnotationListController<T extends AnnotationRecord> {
    /** Ids of the placed annotations, oldest first; a row reads its own record. */
    readonly ids: readonly string[];
    remove: (id: string) => void;
    toggleVisibility: (id: string) => void;
    /** Removes every item and cancels a placement in progress. */
    clearAll: () => void;
    /** Highlights one item on the image, for example while its row is hovered. */
    setHighlighted: (id: string | null) => void;
    /**
     * True when the annotation's plane is not one of its image's own slices:
     * the picture there is interpolated between slices, while the values are
     * read from the voxels. False once the series is no longer in the patient.
     */
    isObliqueToImage: (item: T) => boolean;
    /**
     * Shows the annotation as it was placed: scrolls to its slice and turns
     * the tiles to the plane it was placed in, in the row of its image, or
     * else in a row that draws its image as the fused secondary. Warns, and
     * moves nothing, when no tile draws the annotation's image.
     */
    goTo: (id: string) => void;
}

/**
 * The actions shared by every list of placed annotations: the ids,
 * show/hide, delete, clear all, highlight and go-to. One implementation for
 * every tool, so the same row control always does the same thing. Show/hide
 * and go-to touch only the atom of the annotation they name; remove also
 * drops its id from the list.
 *
 * `anchorOf` must return a point on the annotation's own slice: moving the
 * crosshair there is what scrolls the tiles to it. Go-to moves nothing unless
 * a tile draws the annotation's image, as its row's image or as the fused
 * secondary, so the crosshair is never moved to coordinates that belong to
 * another image and no row turns for an annotation it does not draw; it says
 * so instead, since a row click that moves nothing would otherwise read as a
 * lost click.
 *
 * Define `anchorOf` at module level rather than inline, so the returned
 * callbacks keep their identity across renders.
 */
export function useAnnotationListController<T extends AnnotationRecord>(
    list: AnnotationListState<T>,
    highlightedState: RecoilState<string | null>,
    anchorOf: (item: T) => PointMm
): AnnotationListController<T> {
    const ids = useRecoilValue(list.ids);
    const setHighlighted = useSetRecoilState(highlightedState);
    const imageForSeries = useImageForSeries();

    const remove = useRecoilCallback(
        (callback) => (id: string) => removeAnnotation(callback, list, id),
        [list]
    );

    const toggleVisibility = useRecoilCallback(
        (callback) => (id: string) => {
            updateAnnotation(callback, list, id, (item) => ({
                ...item,
                visible: !item.visible,
            }));
        },
        [list]
    );

    const clearAll = useRecoilCallback(
        (callback) => () => {
            callback.snapshot
                .getLoadable(viewManagerState)
                .getValue()
                ?.interactionContext.toolManager.cancelActivePlacementDraft();
            clearAnnotations(callback, list);
            callback.reset(highlightedState);
        },
        [list, highlightedState]
    );

    const isObliqueToImage = useCallback(
        (item: T) => {
            const grid = imageForSeries(item.plane.seriesInstanceUid)?.grid;
            return (
                grid !== undefined &&
                grid.axisAlong(item.plane.viewDirection) === undefined
            );
        },
        [imageForSeries]
    );

    // Reads the view manager from the snapshot at click time, so the panel
    // does not re-render on every change to it.
    const goTo = useRecoilCallback(
        ({ snapshot }) =>
            (id: string) => {
                const item = snapshot.getLoadable(list.item(id)).getValue();
                if (!item) {
                    return;
                }
                const viewManager = snapshot
                    .getLoadable(viewManagerState)
                    .getValue();
                if (
                    !viewManager ||
                    !goToPlanePoint(viewManager, item.plane, anchorOf(item))
                ) {
                    enqueueSnackbar(
                        localization.t(IMAGE_NOT_SHOWN_MESSAGE_ID),
                        {
                            variant: 'warning',
                        }
                    );
                }
            },
        [list, anchorOf]
    );

    return {
        ids,
        remove,
        toggleVisibility,
        clearAll,
        setHighlighted,
        isObliqueToImage,
        goTo,
    };
}
